import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SubscriptionSection from '../components/SubscriptionSection';
import '../styles/Solar.css';

const Solar = () => {
  const [activeTab, setActiveTab] = useState('gioi-thieu');
  const [step, setStep] = useState(1);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // API configuration
  const API_BASE_URL = "https://phucansolutions.vn" ;

  // Fetch projects data
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 Fetching solar projects...');
        
        // Try to fetch projects by category first
        let response = await fetch(`${API_BASE_URL}/api/projects/category/solar?limit=6`);
        
        if (!response.ok) {
          // If category endpoint fails, try featured projects
          response = await fetch(`${API_BASE_URL}/api/projects/featured?limit=6`);
        }

        if (response.ok) {
          const data = await response.json();
          const processedProjects = data.data?.map(processProject) || [];
          setProjects(processedProjects);
          console.log('✅ Solar projects loaded:', processedProjects.length);
        } else {
          throw new Error('Failed to fetch projects');
        }
        
      } catch (err) {
        console.error('❌ Error fetching solar projects:', err);
        setError(err.message);
        
        // Use fallback data if API fails
        setProjects(getFallbackProjects());
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [API_BASE_URL]);

  // Process project data from API
  const processProject = (project) => ({
    id: project.id,
    title: project.title,
    description: project.description || `Dự án ${project.title} - Hệ thống năng lượng mặt trời chuyên nghiệp`,
    image: getImageUrl(project.featured_image),
    technologies: project.technologies ? (typeof project.technologies === 'string' ? JSON.parse(project.technologies) : project.technologies) : ['Solar Panel', 'Clean Energy'],
    status: project.status || 'completed',
    featured: project.is_featured || true,
    slug: project.slug,
    client: project.client_name,
    views: project.views_count || 0
  });

  // Get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/images/solar-home.jpg';
    if (imagePath.startsWith('/uploads/')) return `${API_BASE_URL}${imagePath}`;
    return imagePath;
  };

  // Fallback projects data
  const getFallbackProjects = () => [
    {
      id: 1,
      title: 'Hệ thống điện mặt trời gia đình - Quận 7',
      description: 'Lắp đặt hệ thống 5kW cho gia đình, tiết kiệm 90% hóa đơn tiền điện, thu hồi vốn trong 6 năm.',
      image: '/images/solar-home.jpg',
      technologies: ['Solar Panel 5kW', 'Grid-tie Inverter'],
      status: 'completed',
      featured: true
    },
    {
      id: 2,
      title: 'Điện mặt trời nhà xưởng - Bình Dương',
      description: 'Hệ thống 100kW cho nhà máy sản xuất, giảm chi phí vận hành và nâng cao hiệu quả kinh doanh.',
      image: '/images/solar-factory.jpg',
      technologies: ['Solar Panel 100kW', 'Industrial Inverter'],
      status: 'completed',
      featured: true
    },
    {
      id: 3,
      title: 'Solar cho trung tâm thương mại - TP.HCM',
      description: 'Dự án 200kW cung cấp năng lượng sạch cho toà nhà thương mại, thể hiện cam kết bảo vệ môi trường.',
      image: '/images/solar-commercial.jpg',
      technologies: ['Solar Panel 200kW', 'Commercial Inverter'],
      status: 'completed',
      featured: true
    },
    {
      id: 4,
      title: 'Điện mặt trời trường học - Đồng Nai',
      description: 'Hệ thống 50kW cho trường học, tạo môi trường giáo dục xanh và tiết kiệm ngân sách vận hành.',
      image: '/images/solar-school.jpg',
      technologies: ['Solar Panel 50kW', 'Educational System'],
      status: 'completed',
      featured: true
    }
  ];

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  // Scroll to specific section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle "TÌM HIỂU THÊM" button clicks
  const handleLearnMore = (type) => {
    scrollToSection('advantages-section');
  };

  const renderGioiThieu = () => (
    <div className="solar-intro-content">
      <h2 className="section-title">Giải pháp cho năng lượng mặt trời</h2>
      <div className="solar-grid">
        {[
          { title: 'Đèn chiếu sáng năng lượng mặt trời', desc: 'Giải pháp chiếu sáng bằng năng lượng mặt trời cho khu vực nông thôn, đô thị và những nơi chưa có điện lưới. Đèn đường giúp tiết kiệm chi phí điện và thân thiện với môi trường.', img: '/images/Rectangle-14.png' },
          { title: 'Hệ thống năng lượng mặt trời cho máy bơm', desc: 'Ứng dụng tại các khu vực không có điện lưới, giúp máy bơm vận hành ổn định nhờ nguồn năng lượng mặt trời.', img: '/images/Rectangle-14-1.png' },
          { title: 'Hệ thống bám tải giảm chi phí điện', desc: 'Hệ thống năng lượng mặt trời bám tải giúp giảm đáng kể chi phí tiền điện hàng tháng cho hộ gia đình, trang trại và doanh nghiệp.', img: '/images/Rectangle-14-2.png' },
          { title: 'Hệ thống Hybrid độc lập điện lưới', desc: 'Giải pháp dùng inverter Hybrid cho các khu vực không có điện lưới hoặc thường xuyên bị cúp điện, đảm bảo nguồn điện liên tục.', img: '/images/Rectangle-14-3.png' }
        ].map((item, idx) => (
          <div key={idx} className="solar-card">
            <img src={item.img} alt={item.title} className="solar-card-image" />
            <div className="solar-card-content">
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
              <button className="learn-more-btn" onClick={() => handleLearnMore('residential')}>TÌM HIỂU THÊM</button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Ưu điểm của giải pháp */}
      <div id="advantages-section" className="advantages-section">
        <h2 className="section-title">Ưu điểm của giải pháp</h2>
        <div className="advantages-content">
          <div className="advantage-item">
          <div className="advantage-image">
              <img src="/images/Rectangle-25.png" alt="Đèn chiếu sáng năng lượng mặt trời" />
            </div>
            <div className="advantage-text">
              <h3>Đèn chiếu sáng năng lượng mặt trời</h3>
              <p><strong>Tiết kiệm điện:</strong> Không sử dụng điện lưới, giảm chi phí vận hành.<br/><br/><strong>Dễ triển khai:</strong> Không cần kéo dây điện, phù hợp mọi địa hình.<br/><br/><strong>Tự động bật/tắt:</strong> Cảm biến ánh sáng tích hợp giúp vận hành thông minh.<br/><br/><strong>Thân thiện môi trường:</strong> Giảm khí thải carbon, bảo vệ môi trường.</p>
            </div>
            
          </div>
          
          <div className="advantage-item">
            <div className="advantage-image">
              <img src="/images/Rectangle-27.png" alt="Hệ thống bám tải" />
            </div>
            <div className="advantage-text">
              <h3>Hệ thống năng lượng mặt trời bám tải</h3>
              <p><strong>Giảm hóa đơn điện:</strong> Ưu tiên dùng điện mặt trời trước, giảm dùng điện EVN.<br/><br/><strong>Chi phí đầu tư hợp lý:</strong> Giá thành rẻ, nhanh thu hồi vốn, giảm chi phí đầu tư ban đầu.<br/><br/><strong>Tối ưu theo phụ tải:</strong> Điều phối công suất theo nhu cầu sử dụng thực tế.<br/><br/><strong>Dễ mở rộng:</strong> Có thể nâng cấp công suất dễ dàng khi nhu cầu tăng.</p>
            </div>
          </div>
          
          <div className="advantage-item">
          <div className="advantage-image">
              <img src="/images/Rectangle-26.png" alt="Hệ thống máy bơm năng lượng mặt trời" />
            </div>
            <div className="advantage-text">
              <h3>Hệ thống NLMT cho máy bơm ở khu vực không có điện lưới</h3>
              <p><strong>Vận hành ổn định:</strong> Hoạt động hiệu quả ở vùng sâu vùng xa, không phụ thuộc điện lưới.<br/><br/><strong>Giảm chi phí nhiên liệu:</strong> Không cần dùng máy phát điện chạy xăng/dầu.<br/><br/><strong>Tự động hóa cao:</strong> Hệ thống điều khiển thông minh, dễ sử dụng.<br/><br/><strong>Phù hợp nông nghiệp:</strong> Tưới tiêu, cấp nước chủ động theo mùa vụ.</p>
            </div>
            
          </div>
          
          <div className="advantage-item">
            <div className="advantage-image">
              <img src="/images/Rectangle-28.png" alt="Hệ thống Hybrid năng lượng mặt trời" />
            </div>
            <div className="advantage-text">
              <h3>Hệ thống Hybrid (cho khu vực không có điện lưới / cúp điện)</h3>
              <p><strong>Hoạt động độc lập:</strong> Vẫn sử dụng điện khi mất lưới hoặc không có điện lưới.<br/><br/><strong>Tích hợp pin lưu trữ:</strong> Lưu trữ điện vào ban ngày, dùng vào ban đêm hoặc khi cúp điện.<br/><br/><strong>Linh hoạt cấu hình:</strong> Có thể hòa lưới, off-grid hoặc kết hợp cả hai.<br/><br/><strong>Độ ổn định cao:</strong> Inverter thông minh, bảo vệ thiết bị điện trong gia đình.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLoiIch = () => (
    <div className="benefits-content">
      <div className="benefits-grid">
        {[
          { icon: '💰', title: 'Tiết kiệm chi phí', desc: 'Giảm 80-90% hóa đơn tiền điện, thu hồi vốn đầu tư trong 5-7 năm, tạo nguồn thu nhập từ việc bán điện cho EVN.' },
          { icon: '🌱', title: 'Thân thiện môi trường', desc: 'Không phát thải khí CO2, giảm thiểu ô nhiễm môi trường, góp phần bảo vệ trái đất cho thế hệ tương lai.' },
          { icon: '⚡', title: 'Độ bền cao', desc: 'Tuổi thọ 25-30 năm, chịu được thời tiết khắc nghiệt, bảo hành dài hạn, bảo trì tối thiểu.' },
          { icon: '🏡', title: 'Tăng giá trị tài sản', desc: 'Tăng giá trị bất động sản, tạo điểm nhấn hiện đại cho ngôi nhà, thể hiện tính bền vững và tiên tiến.' }
        ].map((item, idx) => (
          <div key={idx} className="benefit-card">
            <div className="benefit-header">
              <div className="benefit-icon">
                <span>{item.icon}</span>
              </div>
              <h3>{item.title}</h3>
            </div>
            <p>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderProjects = () => (
    <div className="projects-content">
      <h2 className="section-title">Dự án tiêu biểu</h2>
      
      {/* API Status Indicator */}
      {error && (
        <div className="api-status error">
          ⚠️ Đang sử dụng dữ liệu mẫu (API: {error})
        </div>
      )}
      
      {loading ? (
        <div className="loading-projects">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="projects-grid">
          {projects.map((project) => (
            <div key={project.id} className="project-card">
              <img src={project.image} alt={project.title} className="project-image" />
              <div className="project-content">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div className="project-tech">
                  {project.technologies.slice(0, 3).map((tech, idx) => (
                    <span key={idx} className="tech-tag">{tech}</span>
                  ))}
                  {project.technologies.length > 3 && (
                    <span className="tech-tag extra">+{project.technologies.length - 3}</span>
                  )}
                </div>
                <div className="project-footer">
                  <div className="project-meta">
                    <span className="project-status">{project.status}</span>
                  </div>
                  <button onClick={() => handleViewDetails(project)} className="view-details-btn">
                    Xem chi tiết →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Video Section */}
      <div className="video-section">
        <h3 className="video-title">Video dự án tiêu biểu</h3>
        <div className="video-container">
          <div className="video-player">
            <iframe
              src="https://www.youtube.com/embed/LOLiq4w7SEs"
              title="Hệ thống điện mặt trời gia đình 5kW"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="video-iframe"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'gioi-thieu': return renderGioiThieu();
      case 'loi-ich': return renderLoiIch();
      case 'quy-trinh': return (
        <div className="process-content">
          <h2 className="section-title">Quy trình lắp đặt hệ thống điện mặt trời</h2>
          <p className="process-description">Quy trình lắp đặt hệ thống điện mặt trời chuyên nghiệp từ khảo sát, thiết kế đến vận hành và bảo trì, đảm bảo hiệu quả tối ưu và an toàn tuyệt đối.</p>
          <div className="process-timeline">
            {[
              { title: 'BƯỚC 1: KHẢO SÁT VÀ TƯ VẤN', icon: '/images/1.png', content: 'Khảo sát thực tế mái nhà, đo đạc diện tích, đánh giá hướng mái và góc nghiêng. Tính toán công suất phù hợp, tư vấn phương án tối ưu và báo giá chi tiết.', image: '/images/Rectangle-28.png' },
              { title: 'BƯỚC 2: THIẾT KẾ HỆ THỐNG', icon: '/images/2.png', content: 'Thiết kế sơ đồ hệ thống, bố trí vị trí tấm pin, inverter, tủ DC/AC. Tính toán sản lượng điện dự kiến và hiệu quả kinh tế.', image: '' },
              { title: 'BƯỚC 3: LẬP HỒ SƠ PHÁP LÝ', icon: '/images/3.png', content: 'Hỗ trợ khách hàng làm thủ tục với EVN, xin phép lắp đặt, ký hợp đồng mua bán điện mặt trời.', image: '' },
              { title: 'BƯỚC 4: CUNG CẤP THIẾT BỊ', icon: '/images/4.png', content: 'Cung cấp tấm pin solar, inverter, phụ kiện lắp đặt chính hãng với bảo hành dài hạn từ các thương hiệu uy tín.', image: '' },
              { title: 'BƯỚC 5: THI CÔNG LẮP ĐẶT', icon: '/images/5.png', content: 'Thi công lắp đặt hệ thống theo đúng thiết kế, đảm bảo an toàn và thẩm mỹ. Kiểm tra chất lượng từng công đoạn.', image: '' },
              { title: 'BƯỚC 6: KẾT NỐI LƯỚI ĐIỆN', icon: '/images/6-1.png', content: 'Kết nối hệ thống với lưới điện quốc gia, lắp đặt đồng hồ đo điện 2 chiều, kiểm tra vận hành.', image: '' },
              { title: 'BƯỚC 7: NGHIỆM THU VÀ BÀN GIAO', icon: '/images/7.png', content: 'Kiểm tra toàn bộ hệ thống, đo đạc hiệu suất, hướng dẫn sử dụng và bảo trì, bàn giao hồ sơ kỹ thuật.', image: '' },
              { title: 'BƯỚC 8: BẢO HÀNH VÀ BẢO TRÌ', icon: '/images/8.png', content: 'Cung cấp dịch vụ bảo hành, bảo trì định kỳ, theo dõi hiệu suất hệ thống, hỗ trợ kỹ thuật 24/7.', image: '' }
            ].map((s, idx) => (
              <div key={idx} className={`process-step ${step === idx + 1 ? 'active' : ''}`}>
                <div className={`step-wrapper ${idx % 2 === 0 ? 'left' : 'right'}`}>
                  <button onClick={() => setStep(idx + 1)} className="step-button">
                    <img src={s.icon} alt={s.title} className="step-icon" />
                    <span className="step-title">{s.title}</span>
                  </button>
                  <div className="timeline-dot"></div>
                </div>
                {step === idx + 1 && (
                  <div className="step-detail">
                    <div className="step-detail-content">
                      <h3>{s.title}</h3>
                      <p>{s.content}</p>
                    </div>
                    {s.image && (
                      <div className="step-detail-image">
                        <img src={s.image} alt={s.title} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
      case 'du-an': return renderProjects();
      default: return null;
    }
  };

  return (
    <main className="solar-main">
      {/* Hero Banner */}
      <div className="hero-banner">
        <img src="/images/Rectangle9.png" alt="Solar Energy Banner" className="hero-image" />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>NĂNG LƯỢNG MẶT TRỜI</h1>
          <p>Giải pháp năng lượng sạch, bền vững và tiết kiệm chi phí cho gia đình và doanh nghiệp. Hệ thống điện mặt trời hiệu suất cao với công nghệ tiên tiến nhất.</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="tab-navigation">
        <button 
          onClick={() => setActiveTab('gioi-thieu')}
          className={`tab-btn ${activeTab === 'gioi-thieu' ? 'active' : ''}`}
        >
          GIỚI THIỆU
        </button>
        <button 
          onClick={() => setActiveTab('quy-trinh')}
          className={`tab-btn ${activeTab === 'quy-trinh' ? 'active' : ''}`}
        >
          QUY TRÌNH
        </button>
        <button 
          onClick={() => setActiveTab('du-an')}
          className={`tab-btn ${activeTab === 'du-an' ? 'active' : ''}`}
        >
          DỰ ÁN TIÊU BIỂU
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>

      {/* Project Modal */}
      {isModalOpen && selectedProject && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            <img src={selectedProject.image} alt={selectedProject.title} className="modal-image" />
            <div className="modal-body">
              <h2>{selectedProject.title}</h2>
              <p>{selectedProject.description}</p>
              <div className="modal-tech">
                {selectedProject.technologies.map((tech, idx) => (
                  <span key={idx} className="tech-tag">{tech}</span>
                ))}
              </div>
              {selectedProject.client && (
                <div className="project-info">
                  <p><strong>Khách hàng:</strong> {selectedProject.client}</p>
                </div>
              )}
              {selectedProject.views > 0 && (
                <div className="project-stats">
                  <p>👁️ {selectedProject.views} lượt xem</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Subscription Section */}
      <SubscriptionSection />
    </main>
  );
};

export default Solar; 